import { useState } from 'react';
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { Player, Team } from '../Model';

export function useDragHandlers(
  teams: Team[],
  setTeams: React.Dispatch<React.SetStateAction<Team[]>>
) {
  const [activeId, setActiveId] = useState<string | number | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    // Early exit if there's no active drag operation
    if (!activeId) {
      return;
    }
    
    setActiveId(null);

    if (!over) return;

    const activeIdStr = active.id as string | number;
    const overIdStr = over.id as string | number;

    // Extract numeric IDs from prefixed strings or use directly if numeric
    let draggedPlayerId: number | null = null;
    
    if (typeof activeIdStr === 'string' && activeIdStr.toString().startsWith('player-')) {
      draggedPlayerId = parseInt(activeIdStr.replace('player-', ''));
    } else if (typeof activeIdStr === 'number') {
      draggedPlayerId = activeIdStr;
    }
    
    if (draggedPlayerId === null) return;

    // Find which team the dragged player is currently in
    let activePlayer: Player | null = null;
    let sourceTeamIndex = -1;
    
    for (let i = 0; i < teams.length; i++) {
      const foundPlayer = teams[i].players?.find(p => p.id === draggedPlayerId);
      if (foundPlayer) {
        activePlayer = foundPlayer;
        sourceTeamIndex = i;
        break;
      }
    }

    if (!activePlayer || sourceTeamIndex === -1) return;

    // Check if we're dropping on another player first (for swapping/repositioning)
    let overPlayerId: number | null = null;
    
    if (typeof overIdStr === 'string' && overIdStr.startsWith('player-')) {
      overPlayerId = parseInt(overIdStr.replace('player-', ''));
    } else if (typeof overIdStr === 'number') {
      overPlayerId = overIdStr;
    }
    
    if (overPlayerId !== null) {
      let overPlayer: Player | null = null;
      let overTeamIndex = -1;

      for (let i = 0; i < teams.length; i++) {
        const foundPlayer = teams[i].players?.find(p => p.id === overPlayerId);
        if (foundPlayer) {
          overPlayer = foundPlayer;
          overTeamIndex = i;
          break;
        }
      }

      if (overPlayer && overPlayer.id !== draggedPlayerId && activePlayer) {
        if (sourceTeamIndex === overTeamIndex) {
          // Swap within the same team
          setTeams(prevTeams => {
            const newTeams = [...prevTeams];
            const teamPlayers = [...(newTeams[sourceTeamIndex].players || [])];
            
            const activeIndex = teamPlayers.findIndex(p => p.id === draggedPlayerId);
            const overIndex = teamPlayers.findIndex(p => p.id === overPlayer!.id);
            
            if (activeIndex !== -1 && overIndex !== -1) {
              // Swap the players
              [teamPlayers[activeIndex], teamPlayers[overIndex]] = [teamPlayers[overIndex], teamPlayers[activeIndex]];
              
              newTeams[sourceTeamIndex] = {
                ...newTeams[sourceTeamIndex],
                players: teamPlayers
              };
            }
            
            return newTeams;
          });
        } else {
          // Swap between different teams - replace each player in their exact position
          setTeams(prevTeams => {
            const newTeams = [...prevTeams];
            
            // Replace the dragged player with the target player in source team
            newTeams[sourceTeamIndex] = {
              ...newTeams[sourceTeamIndex],
              players: (newTeams[sourceTeamIndex].players || []).map(p => 
                p.id === draggedPlayerId ? overPlayer! : p
              )
            };
            
            // Replace the target player with the dragged player in target team
            newTeams[overTeamIndex] = {
              ...newTeams[overTeamIndex],
              players: (newTeams[overTeamIndex].players || []).map(p => 
                p.id === overPlayer!.id ? activePlayer! : p
              )
            };
            
            return newTeams;
          });
        }
        return; // Early return only when a swap actually happened
      }
    }

    // Check if we're dropping on a team container
    if (typeof overIdStr === 'string' && overIdStr.startsWith('team-')) {
      const targetTeamId = parseInt(overIdStr.replace('team-', ''));
      const targetTeamIndex = teams.findIndex(team => team.id === targetTeamId);
      
      if (targetTeamIndex !== -1 && sourceTeamIndex !== targetTeamIndex) {
        setTeams(prevTeams => {
          const newTeams = [...prevTeams];
          
          // Remove player from source team
          newTeams[sourceTeamIndex] = {
            ...newTeams[sourceTeamIndex],
            players: (newTeams[sourceTeamIndex].players || []).filter(p => p.id !== draggedPlayerId)
          };
          
          // Add player to target team
          newTeams[targetTeamIndex] = {
            ...newTeams[targetTeamIndex],
            players: [...(newTeams[targetTeamIndex].players || []), activePlayer!]
          };
          
          return newTeams;
        });
      }
      return;
    }

    // Check for direct team ID (backward compatibility with tests)
    if (typeof overIdStr === 'number') {
      const targetTeamIndex = teams.findIndex(team => team.id === overIdStr);
      
      if (targetTeamIndex !== -1 && sourceTeamIndex !== targetTeamIndex) {
        setTeams(prevTeams => {
          const newTeams = [...prevTeams];
          
          // Remove player from source team
          newTeams[sourceTeamIndex] = {
            ...newTeams[sourceTeamIndex],
            players: (newTeams[sourceTeamIndex].players || []).filter(p => p.id !== draggedPlayerId)
          };
          
          // Add player to target team
          newTeams[targetTeamIndex] = {
            ...newTeams[targetTeamIndex],
            players: [...(newTeams[targetTeamIndex].players || []), activePlayer!]
          };
          
          return newTeams;
        });
      }
      return;
    }
  };

  // Find active player across all teams
  const activePlayer = activeId ? (() => {
    let playerId: number | null = null;
    
    if (typeof activeId === 'string' && activeId.startsWith('player-')) {
      playerId = parseInt(activeId.replace('player-', ''));
    } else if (typeof activeId === 'number') {
      playerId = activeId;
    }
    
    return playerId !== null 
      ? teams.flatMap(team => team.players || []).find(p => p.id === playerId) || null
      : null;
  })() : null;

  return {
    activePlayer,
    handleDragStart,
    handleDragEnd,
  };
}
